/**
 * Empty state genérico para secciones sin datos.
 *
 * Sustituye los textos pelados ("Sin datos.") que dejan al usuario
 * dudando si la app está rota o si los datos no existen. Comunica la
 * realidad: qué falta, por qué, y opcionalmente qué hacer al respecto.
 *
 * **Variantes** (`variant` prop):
 * - `coming-soon` (default): contenido en preparación. Tono optimista —
 *   ícono de reloj, color slate. Para cuando los seeds de un dataset
 *   aún no han corrido o el feature no se ha terminado de poblar.
 * - `no-results`: el filtro/búsqueda no devolvió nada. Ícono de lupa.
 *   Sugiere ajustar criterios.
 * - `error-soft`: estado degradado por una falla controlada. Ícono de
 *   alerta. Color ámbar.
 *
 * **Cuándo NO usarlo:**
 * - Errores de red / 500 → componente ErrorCard dedicado, mantiene el
 *   tono rojo y evita que parezca "feature incompleta".
 * - Loading → skeleton (`<Skeleton />`), no un mensaje.
 *
 * **A11y:**
 * - role="status" para que el screen reader anuncie el cambio cuando un
 *   filtro reduce el listado a 0.
 * - El icono es decorativo (`aria-hidden`); el mensaje viaja por texto.
 */

import type { ReactNode } from "react";

export type EmptyStateVariant = "coming-soon" | "no-results" | "error-soft";

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  /** Título corto (1 línea, frase nominal). */
  title: string;
  /** Descripción (1-2 oraciones). */
  description?: string;
  /** Pista opcional con tono más quiet — qué hará la plataforma o cuándo. */
  hint?: string;
  /** Acción opcional (ej. botón "Ajustar filtros"). */
  action?: ReactNode;
  className?: string;
}

const VARIANT_STYLES: Record<
  EmptyStateVariant,
  { container: string; icon: string; iconBg: string }
> = {
  "coming-soon": {
    container: "border-slate-200 bg-white",
    icon: "text-slate-400",
    iconBg: "bg-slate-100",
  },
  "no-results": {
    container: "border-slate-200 bg-white",
    icon: "text-brand-500",
    iconBg: "bg-brand-50",
  },
  "error-soft": {
    container: "border-amber-200 bg-amber-50/50",
    icon: "text-amber-600",
    iconBg: "bg-amber-100",
  },
};

function VariantIcon({ variant }: { variant: EmptyStateVariant }) {
  // Iconos inline — más liviano que un icon-font/lib runtime.
  if (variant === "no-results") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
        className="h-6 w-6"
      >
        <circle cx="11" cy="11" r="7" />
        <path strokeLinecap="round" d="M21 21l-4.3-4.3" />
      </svg>
    );
  }
  if (variant === "error-soft") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v4m0 4h.01M10.3 3.86l-8.18 14.18A2 2 0 003.83 21h16.34a2 2 0 001.71-2.96L13.7 3.86a2 2 0 00-3.4 0z"
        />
      </svg>
    );
  }
  // coming-soon (default) — clock con ticks suaves.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
      className="h-6 w-6"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 7v5l3 2" />
    </svg>
  );
}

export function EmptyState({
  variant = "coming-soon",
  title,
  description,
  hint,
  action,
  className,
}: EmptyStateProps) {
  const styles = VARIANT_STYLES[variant];
  return (
    <div
      role="status"
      data-testid="empty-state"
      data-variant={variant}
      className={`flex flex-col items-center rounded-2xl border border-dashed p-8 text-center ${styles.container} ${className ?? ""}`}
    >
      <span
        aria-hidden="true"
        className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full ${styles.iconBg} ${styles.icon}`}
      >
        <VariantIcon variant={variant} />
      </span>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description && <p className="mt-1.5 max-w-md text-sm text-slate-600">{description}</p>}
      {hint && <p className="mt-3 max-w-md text-xs text-slate-500">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
